import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, openCheckoutSync, resolveCheckout } from "@/lib/api";
import { Button } from "@/components/ui/Primitives";
import { AuthLayout } from "@/pages/Login";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";
import { ArrowRight, Building2, Home, Eye } from "lucide-react";
import { ClosedEyeLashes } from "@/components/icons/ClosedEyeLashes";
import SmartChatBubble from "@/components/SmartChatBubble";
import { MathCaptcha, useCaptcha } from "@/components/MathCaptcha";

export default function Signup() {
  const { signup, setRole } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const roleHint = params.get("role") === "tenant" ? "tenant" : params.get("role") === "landlord" ? "landlord" : null;
  const planHint = params.get("plan"); // starter | professional | enterprise — auto-launch Stripe after signup
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  // Inline role selector. Default to whatever was passed via ?role= URL hint,
  // else "landlord" (the dominant signup path). We persist the user's pick
  // inside the same signup transaction so they never hit the /role-select
  // intermediate screen — fewer steps + no race conditions on the role write.
  const [chosenRole, setChosenRole] = useState(roleHint || "landlord");
  const [realtorProfileUrl, setRealtorProfileUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const captcha = useCaptcha();

  const cfg = roleHint === "tenant" ? {
    title: "Create a tenant account",
    subtitle: "Pay rent and message your landlord in one place.",
    badge: "Tenant signup",
    icon: Home,
  } : {
    title: roleHint === "landlord" ? "Create a landlord account" : "Create your account",
    subtitle: "Set up in about a minute.",
    badge: roleHint === "landlord" ? "Landlord signup" : "Get started",
    icon: Building2,
  };

  usePageTitle(`${cfg.title} — Central Rentals Canada`);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8 || !/\p{L}/u.test(password) || !/\p{N}/u.test(password)) {
      setError("Use at least 8 characters, including a letter and a number.");
      return;
    }
    if (new TextEncoder().encode(password).length > 72) {
      setError("This password is too long. Use no more than 72 UTF-8 bytes.");
      return;
    }
    setSubmitting(true);
    // Realtor.ca is invitation-only and does not publish a public OAuth
    // endpoint, so we validate the profile URL pattern locally + store
    // it on the user record — the Central Rentals badge team then
    // verifies manually via a lightweight scrape.
    if (realtorProfileUrl.trim()) {
      const ok = /^https?:\/\/(www\.)?realtor\.ca\//i.test(realtorProfileUrl.trim());
      if (!ok) {
        setError("Realtor.ca URL must start with https://www.realtor.ca/…");
        setSubmitting(false);
        return;
      }
    }
    // Open a blank tab synchronously NOW so we keep the user-gesture for
    // a potential Stripe redirect right after signup.
    const popup = planHint ? openCheckoutSync() : null;
    try {
      await signup(email.trim(), password, name.trim() || email.trim().split("@")[0], chosenRole, captcha.headers(), phone.trim() || null);
      // Attach the Realtor.ca profile URL for the badge team.
      if (realtorProfileUrl.trim() && chosenRole === "landlord") {
        try {
          await api.post("/users/me/realtor-profile", { url: realtorProfileUrl.trim() });
        } catch { /* best-effort, badge team can back-fill */ }
      }
      // Google Ads conversion event — tells Google Smart Bidding that
      // this click → conversion mapping worked. Without this, Google's
      // recommendations dashboard shows "Conversion tracking incomplete"
      // and Smart Bidding can't learn. The helper safely no-ops if env
      // vars REACT_APP_GOOGLE_ADS_CONVERSION_ID / _LABEL aren't set.
      try {
        const { fireConversion } = await import("@/lib/google-tag");
        fireConversion({
          valueCad: 60,
          transactionId: `signup_${Date.now()}`,
          email: email.trim(),
        });
      } catch { /* never block signup UX */ }
      // Registration alerts are persisted and sent by the signup endpoint.
      // Auto-redeem any stashed referral code from a public listing visit.
      try {
        const stashed = localStorage.getItem("crc_ref");
        if (stashed) {
          await api.post("/referrals/redeem", { code: stashed }).catch(() => {});
          localStorage.removeItem("crc_ref");
        }
      } catch {}
      // Role is already passed to signup() atomically, but call setRole
      // as a safety net in case the upstream auth backend ignored the
      // role field in the signup payload (older deployments).
      try { await setRole(chosenRole); } catch {}
      // If the visitor came from a pricing CTA, immediately start Stripe
      // Checkout for that plan. They land on Stripe, then return_url brings
      // them back into the app with their subscription active.
      if (planHint) {
        try {
          // Frontend plan slugs (`growth`, `portfolio`) → backend keys.
          const PLAN_ALIAS = { growth: "professional", portfolio: "enterprise" };
          const backendPlanKey = PLAN_ALIAS[planHint] || planHint;
          // If the visitor arrived via the exit-intent offer, forward the
          // coupon to Stripe so the first-month price is halved.
          const coupon = params.get("coupon") || null;
          const payload = { plan_key: backendPlanKey };
          if (coupon) payload.coupon = coupon;
          const { data } = await api.post("/subscription/checkout", payload);
          if (data?.url) {
            resolveCheckout(popup, data.url);
            toast.success(coupon
              ? "Account created — 50% off applied at checkout."
              : "Account created — finishing checkout…");
            nav(`/billing?plan=${encodeURIComponent(planHint)}&autoOpen=1${coupon ? `&coupon=${encodeURIComponent(coupon)}` : ""}`, { replace: true });
            return;
          }
          throw new Error("Checkout returned no URL");
        } catch (err) {
          // The account exists; only checkout failed. Say so and log it
          // instead of silently dropping the user on the dashboard unpaid.
          toast.error("Your account was created, but checkout didn't open. Go to Billing to finish choosing your plan.");
          api.post("/client-errors", {
            kind: "checkout_after_signup",
            message: `POST /subscription/checkout (${planHint}) → ${err?.response?.status || err?.code || err?.message || "network"}`,
            path: window.location.pathname,
          }).catch(() => {});
        }
        // Checkout failed (no Stripe, etc.) — fall through to dashboard.
        if (popup) { try { popup.close(); } catch {} }
      }
      toast.success("Account created");
      // Role is already set above, so always go directly to the dashboard
      // (skip the /role-select intermediate page entirely).
      nav("/dashboard", { replace: true });
    } catch (err) {
      if (popup) { try { popup.close(); } catch {} }
      const msg = err?.response?.data?.detail || "Signup failed.";
      if (/security\s+check|captcha/i.test(String(msg))) captcha.refresh();
      setError(typeof msg === "string" ? msg : "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Create your account" subtitle="Start managing properties or paying rent in under 90 seconds." accent={cfg.badge} PortalIcon={cfg.icon}>
      <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
        <RoleToggle value={chosenRole} onChange={setChosenRole}/>
        <SignupInput label="Full name (optional)" value={name} onChange={(e) => setName(e.target.value)} testid="signup-name-input"/>
        <SignupInput label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required testid="signup-email-input"/>
        <SignupInput label="Phone (optional — we'll text you setup tips)" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} testid="signup-phone-input" autoComplete="tel"/>
        <SignupInput label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required testid="signup-password-input" showToggle helperText="At least 8 characters, including a letter and a number"/>
        {chosenRole === "landlord" && (
          <div>
            <SignupInput
              label="Realtor.ca profile URL (optional — get a verified badge)"
              type="url"
              value={realtorProfileUrl}
              onChange={(e) => setRealtorProfileUrl(e.target.value)}
              testid="signup-realtor-url-input"
              autoComplete="url"
            />
            <p className="mt-1 text-[11px] text-slate-500">We'll manually verify + add a "Realtor.ca licensed" badge to your listings. Realtor.ca doesn't offer public sign-in so this is the fastest verified route.</p>
          </div>
        )}
        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3" data-testid="signup-error">{error}</div>}
        <MathCaptcha {...captcha} testId="signup-captcha" />
        <button
          type="submit"
          disabled={submitting}
          data-testid="signup-submit-button"
          className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-base font-bold shadow-sm transition-colors"
        >
          {submitting ? "Creating account…" : "Create my account"}
        </button>
        <p className="text-xs text-slate-500 leading-relaxed text-center">By creating an account you agree to our <Link to="/legal/terms" className="underline hover:text-slate-900">Terms</Link> and <Link to="/legal/privacy" className="underline hover:text-slate-900">Privacy Policy</Link>. Cancel any time.</p>
      </form>
      {/* Divider + login redirect */}
      <div className="mt-7 flex items-center gap-3 text-xs text-slate-400">
        <div className="flex-1 h-px bg-slate-200"/>
        <span>or</span>
        <div className="flex-1 h-px bg-slate-200"/>
      </div>
      <div className="mt-6 text-center text-sm text-slate-600" data-testid="signup-login-strip">
        Already have an account?{" "}
        <Link to={`/login/${chosenRole}`} className="font-bold text-slate-900 hover:underline underline-offset-4 inline-flex items-center gap-1" data-testid="signup-to-login-link">
          Log in <ArrowRight className="w-4 h-4"/>
        </Link>
      </div>
      {/* Ask Mark surfaces during sign-up + onboarding for high-intent help */}
      <SmartChatBubble />
    </AuthLayout>
  );
}

function RoleToggle({ value, onChange }) {
  return (
    <div data-testid="signup-role-toggle">
      <div className="text-sm font-bold text-slate-900 mb-2">I am a…</div>
      <div role="radiogroup" aria-label="Account type" className="flex p-1 rounded-full bg-slate-100">
        <button type="button" role="radio" aria-checked={value === "landlord"}
          onClick={() => onChange("landlord")} data-testid="signup-role-landlord"
          className={`flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full text-sm font-semibold transition-colors ${value === "landlord" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>
          <Building2 className="w-4 h-4"/> Landlord
        </button>
        <button type="button" role="radio" aria-checked={value === "tenant"}
          onClick={() => onChange("tenant")} data-testid="signup-role-tenant"
          className={`flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full text-sm font-semibold transition-colors ${value === "tenant" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>
          <Home className="w-4 h-4"/> Tenant
        </button>
      </div>
    </div>
  );
}

 
function RoleButton({ active, onClick, icon: Icon, title, subtitle, testid }) {
  return (
    <button type="button" onClick={onClick} role="radio" aria-checked={active}
      data-testid={testid}
      className={`text-left px-3.5 py-3 rounded-md border-2 transition-all flex items-start gap-2.5
        ${active
          ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-100"
          : "border-slate-200 bg-white hover:border-slate-300"}`}>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-none
        ${active ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>
        <Icon className="w-4 h-4"/>
      </div>
      <div className="min-w-0">
        <div className="font-bold text-sm text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500 leading-tight">{subtitle}</div>
      </div>
    </button>
  );
}

function DarkInput({ label, testid, required, ...props }) {
  return (
    <label className="block">
      {label && (
        <div className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
          <span>{label}</span>
          {required && <span className="text-rose-600 font-bold ml-0.5">*</span>}
        </div>
      )}
      <input
        data-testid={testid}
        required={required}
        aria-required={required ? "true" : undefined}
        className="w-full bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-md px-3.5 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all shadow-sm"
        {...props}
      />
    </label>
  );
}

function SignupInput({ label, testid, required, showToggle, type = "text", helperText, ...props }) {
  const [visible, setVisible] = useState(false);
  const actualType = showToggle ? (visible ? "text" : "password") : type;
  return (
    <label className="block">
      {label && (
        <div className="text-sm font-bold text-slate-900 mb-2">
          {label}
          {required && <span className="text-rose-600 ml-1">*</span>}
        </div>
      )}
      <div className="relative">
        <input
          data-testid={testid}
          required={required}
          type={actualType}
          aria-required={required ? "true" : undefined}
          className={`w-full h-14 rounded-2xl bg-slate-50 border border-slate-200 focus:border-slate-900 text-slate-900 placeholder:text-slate-400 px-5 ${showToggle ? "pr-12" : ""} text-base focus:ring-2 focus:ring-slate-900/10 outline-none transition-colors`}
          {...props}
        />
        {showToggle && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            data-testid={`${testid}-toggle`}
            aria-label={visible ? "Hide password" : "Show password"}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
          >
            {visible ? <Eye className="w-5 h-5"/> : <ClosedEyeLashes className="w-5 h-5"/>}
          </button>
        )}
      </div>
      {helperText && (
        <div className="mt-1.5 text-[11px] text-slate-500">{helperText}</div>
      )}
    </label>
  );
}
